import React, { useState } from "react";

function CourseContentBasic({ data }) {
  const [activeTab, setActiveTab] = useState(0);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);

  const displayTabs = [
    { label: "About", key: "about" },
    { label: "Sessions", key: "sessions" },
    { label: "Curated By", key: "created_by" },
    { label: "Endorsed By", key: "endorsed_by" },
    { label: "Resources", key: "resources" },
  ].filter(tab => {
    if (tab.key === "sessions") {
      return (data.sessions?.length ?? 0) > 0;
    }
    if (tab.key === "endorsed_by") {
      return (data.course_organisation?.length ?? 0) > 0;
    }
    return true;
  });

  const AWS = process.env.REACT_APP_AWS_HOST;

  const renderEmptyState = (message) => (
    <div className="text-center py-4">
      <div className="mb-3">
        <i className="ri-information-line fs-2 text-muted"></i>
      </div>
      <p className="text-muted mb-0">{message}</p>
    </div>
  );

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  const handleMobileResourceClick = (material) => {
    setSelectedResource(material);
    setShowMobilePopup(true);
  };

  const handleViewResource = () => {
    if (selectedResource) {
      window.open(`${AWS}${selectedResource.material_file}`, '_blank');
    }
    setShowMobilePopup(false);
  };

  const handleDownloadResource = async () => {
    if (selectedResource) {
      const fileUrl = `${AWS}${selectedResource.material_file}`;
      
      try {
        // Try fetch approach first for better download control
        const response = await fetch(fileUrl);
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = selectedResource.material_title || 'download';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          throw new Error('Fetch failed');
        }
      } catch (error) {
        // Fallback to direct link approach
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = selectedResource.material_title || 'download';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
    setShowMobilePopup(false);
  };

  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  return (
    <div>
      <div>
        <ul className="nav nav-pills arrow-navtabs nav-success bg-light mb-3" role="tablist">
          {displayTabs.map(({ label }, idx) => (
            <li className="nav-item" key={label}>
              <a
                className={`nav-link${idx === activeTab ? " active" : ""}`}
                href="#"
                role="tab"
                onClick={(e) => {
                  e.preventDefault();
                  handleTabClick(idx);
                }}
              >
                <span className="d-block d-sm-none" style={{ fontSize: "11px" }}>{label}</span>
                <span className="d-none d-sm-block fs-7">{label}</span>
              </a>
            </li>
          ))}
        </ul>

        <div className="tab-content text-muted">
          {displayTabs.map(({ key, label }, idx) => (
            <div
              className={`tab-pane fade${idx === activeTab ? " show active" : ""}`}
              id={`tab-${idx}`}
              role="tabpanel"
              key={key}
              style={{ display: idx === activeTab ? 'block' : 'none' }}
            >
              <div className="card">
                <div className="card-body">
                  {key === "about" && (
                    data.description ? (
                      <p className="fs-6" dangerouslySetInnerHTML={{ __html: data.description }} />
                    ) : renderEmptyState("No description available")
                  )}

                  {key === "sessions" && (
                    <>
                      <h6 className="text-dark fw-semibold mb-2">Upcoming Sessions</h6>
                      {(data.sessions?.length ?? 0) > 0 ? (
                        <ul className="list-group list-group-sm">
                          {data.sessions.map((session, i) => (
                            <li className="list-group-item py-2 px-3" key={i}>
                              <strong>{session.startDate} - {session.endDate}</strong>
                              <div className="text-muted small">{session.timing}</div>
                              <div className="text-muted small">{session.mode}</div>
                            </li>
                          ))}
                        </ul>
                      ) : renderEmptyState("No upcoming sessions scheduled")}
                    </>
                  )}

                  {key === "created_by" && (
                    <>
                      {(data?.instructors?.length ?? 0) > 0 ? (
                        data?.instructors.map((instructor, i) => (
                          <div key={i} className="d-flex position-relative mb-2">
                            <img 
                              src={`${AWS}${instructor.profile_image}`}
                              onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${instructor.first_name}+${instructor.last_name}&background=random`;
                              }}
                              className="flex-shrink-0 me-2 rounded-circle" 
                              alt={`${instructor.first_name} ${instructor.last_name}`}
                              width="40"
                              height="40"
                            />
                            <div className={instructor?.description ? "flex-grow-1 align-items-center" : "d-flex align-items-center"}>
                              <h5 className="mb-0">{instructor.first_name} {instructor.last_name}</h5>
                              {instructor?.description && <p>{instructor?.description}</p>}
                            </div>
                          </div>
                        ))
                      ) : renderEmptyState("No instructors assigned to this course yet")}
                    </>
                  )}

                  {key === "endorsed_by" && (
                    <>
                      <h6 className="text-dark fw-semibold mb-2">Endorsed By</h6>
                      {(data.course_organisation?.length ?? 0) > 0 ? (
                        <div className="d-flex flex-wrap gap-3">
                          {data.course_organisation.map((org, i) => (
                            <div key={i} className="d-flex align-items-center gap-2">
                              <img src={`${AWS}${org.organisation_logo}`} alt={org.organisation_name} height="40" />
                              <strong>{org.organisation_name}</strong>
                            </div>
                          ))}
                        </div>
                      ) : renderEmptyState("No Data Available")}

                    </>
                  )}

                  {key === "resources" && (
                    <>
                      <h5 className="card-title mb-3">Downloadable Materials</h5>
                      {(data.materials?.length ?? 0) > 0 ? (
                        <div className="vstack gap-2">
                          {data.materials.map((material, i) => (
                            <div key={i} className="border rounded border-dashed p-2">
                              <div className="d-flex align-items-center">
                                {/* Clickable area for both mobile and desktop (entire row except desktop download button) */}
                                <div 
                                  className="d-flex align-items-center flex-grow-1"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    if (window.innerWidth <= 768) {
                                      // Mobile - show popup
                                      handleMobileResourceClick(material);
                                    } else {
                                      // Desktop - direct download
                                      const link = document.createElement('a');
                                      link.href = `${AWS}${material.material_file}`;
                                      link.download = material.material_title || 'download';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }
                                  }}
                                >
                                  <div className="flex-shrink-0 me-3">
                                    <div className="avatar-sm">
                                      <div className="avatar-title bg-light text-primary rounded fs-24">
                                        <i className="ri-file-line"></i>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex-grow-1 overflow-hidden">
                                    <h5 className="fs-13 mb-1 text-wrap">
                                      <span className="text-body">
                                        {material.material_title}
                                      </span>
                                    </h5>
                                  </div>
                                </div>

                                <div className="flex-shrink-0 ms-2">
                                  {/* Desktop - Direct download button (still available as separate option) */}
                                  <a
                                    href={`${AWS}${material.material_file}`}
                                    download
                                    className="btn btn-icon text-muted btn-sm fs-18 d-none d-md-inline-flex"
                                  >
                                    <i className="ri-download-2-line"></i>
                                  </a>
                                  
                                  {/* Mobile - Show popup button */}
                                  <button
                                    onClick={() => handleMobileResourceClick(material)}
                                    className="btn btn-icon text-muted btn-sm fs-18 d-inline-flex d-md-none"
                                  >
                                    <i className="ri-more-2-line"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : renderEmptyState("No downloadable materials available for this course")}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Popup Modal */}
      {showMobilePopup && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Resource Options</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMobilePopup(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  <strong>{selectedResource?.material_title}</strong>
                </p>
                <p className="text-muted small">Choose an action for this resource:</p>
              </div>
              <div className="modal-footer d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary flex-fill"
                  onClick={handleViewResource}
                >
                  <i className="ri-eye-line me-1"></i>
                  View
                </button>
                <button
                  type="button"
                  className="btn btn-primary flex-fill"
                  onClick={handleDownloadResource}
                >
                  <i className="ri-download-2-line me-1"></i>
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
        ul li{
          white-space: normal !important;
        }
        .modal.show {
          display: block !important;
        }
        /* Clickable resource row for both mobile and desktop */
        .border.rounded.border-dashed:hover {
          background-color: rgba(0,0,0,0.02);
          transition: background-color 0.2s ease;
        }
        .border.rounded.border-dashed:active {
          background-color: rgba(0,0,0,0.05);
        }
        /* Ensure the clickable area shows pointer cursor */
        .border.rounded.border-dashed .d-flex.align-items-center.flex-grow-1 {
          cursor: pointer !important;
        }
        `}
      </style>
    </div>
  );
}

export default CourseContentBasic;